/**
 * The admin MUI theme, mirrored from `adminTokens` so `sx` and plain CSS read
 * the same values. Nothing here should hold a literal colour, radius or size —
 * add it to `tokens.ts` and reference it.
 */
import { createTheme } from '@mui/material/styles';
import { adminTokens } from './tokens';

const { button, color, radius, shadow, text, font } = adminTokens;

export const adminTheme = createTheme({
  tokens: adminTokens,
  palette: {
    mode: 'light',
    primary: { main: color.accent },
    success: { main: color.success },
    warning: { main: color.warn },
    error: { main: color.danger },
    background: { default: color.bg, paper: color.surface },
    text: { primary: color.ink, secondary: color.ink3, disabled: color.ink4 },
    divider: color.line,
  },
  shape: {
    borderRadius: radius.card,
  },
  typography: {
    fontFamily: font.sans,
    h1: text.h1,
    h2: text.h2,
    featuredEvent: text.featuredEvent,
    kpiValue: text.kpiValue,
    cardTitle: text.cardTitle,
    bodyLabel: text.bodyLabel,
    tab: text.tab,
    bodySecondary: text.bodySecondary,
    meta: text.meta,
    eyebrow: text.eyebrow,
    counterBadge: text.counterBadge,
    // Spec §2 pairs button with the body label (14 / 500) and §6 adds the
    // -0.005em tracking. Spread the token rather than restating the size and
    // weight — the literal 600 here was carried over from the pre-token theme
    // and rendered every MUI Button one weight step heavy.
    // MUI's own line-height (1.75) is left to stand: §6 gives none, and a
    // button's label is vertically centred by its padding regardless.
    button: {
      ...text.bodyLabel,
      letterSpacing: '-0.005em',
      textTransform: 'none',
    },
  },
  components: {
    MuiTypography: {
      defaultProps: {
        variantMapping: {
          featuredEvent: 'h2',
          kpiValue: 'p',
          cardTitle: 'h3',
          bodyLabel: 'span',
          tab: 'span',
          bodySecondary: 'p',
          meta: 'span',
          eyebrow: 'span',
          counterBadge: 'span',
        },
      },
    },
    MuiButton: {
      defaultProps: {
        // The design has no elevation on buttons — §7 rules out shadow gimmicks,
        // and §5 gives the card the only shadow in the system.
        disableElevation: true,
      },
      styleOverrides: {
        // Only the shape is unconditional. The padding is NOT: it is scoped to
        // the default size below, because `root` lands after MUI's own size
        // styles and would flatten `size="small"` into header-pill geometry —
        // 30 call sites use small for dense table-row actions.
        root: {
          borderRadius: radius.pill,
        },
      },
      // §6's ghost / outline / primary are all accent-coloured, so they are the
      // `color="primary"` case and nothing else. Scoping them this way is what
      // lets a destructive button stay red: `remove-confirmation-dialog` renders
      // `color="error" variant="contained"`, and an unscoped `contained`
      // override would repaint it accent blue while still calling itself a
      // delete. Colour is the signal there; the pill above is just the shape.
      variants: [
        {
          // §6's `9px 18px` describes the header pill — a default-size button.
          props: { size: 'medium' as const },
          style: { padding: button.padding },
        },
        {
          // Ghost. §6 gives it accent text on an --accent-soft hover; the
          // prototype's `.m-btn-ghost` uses --ink-3 on a neutral hover instead,
          // and prose wins here for the same reason it does for the geometry.
          props: { variant: 'text' as const, color: 'primary' as const },
          style: {
            color: color.accent,
            '&:hover': { backgroundColor: color.accentSoft },
          },
        },
        {
          props: { variant: 'outlined' as const, color: 'primary' as const },
          style: {
            backgroundColor: color.surface,
            color: color.accent,
            borderColor: button.outlineBorder,
            '&:hover': {
              backgroundColor: color.accentSoft,
              borderColor: button.outlineBorderHover,
            },
          },
        },
        {
          props: { variant: 'contained' as const, color: 'primary' as const },
          style: {
            backgroundColor: color.accent,
            color: color.surface,
            '&:hover': { backgroundColor: button.primaryHover },
          },
        },
      ],
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
        },
        elevation1: {
          boxShadow: shadow.card,
        },
      },
    },
    /*
     * MUI's form control, aligned to the design's (CAE-1556).
     *
     * The kit's own `AdminInput` / `AdminSelect` are built on bare `<input>`
     * and `<select>`, and every redesigned screen uses them. These overrides
     * are for the forms CAE-1556 brought over that `AdminInput` cannot
     * express: a `select` with `MenuItem` children, a `multiline` JSON editor,
     * a numeric field with `inputMode`. Growing the kit control four ways for
     * three call sites would be a redesign of those forms rather than the
     * mechanical alignment this ticket is.
     *
     * So MUI's control keeps its own shape — label, fieldset, notch — and
     * borrows the design's values: `.m-input-control`'s 8px radius,
     * `--line-strong` edge, accent focus and 13.5px type. It reads as the same
     * family beside an `AdminInput` without either pretending to be the other.
     *
     * The border lives on the notched outline rather than the root, because
     * that is the element MUI actually paints; writing it on the root would
     * draw a second edge inside the first.
     */
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: radius.control,
          backgroundColor: color.surface,
          '& .MuiOutlinedInput-notchedOutline': {
            borderWidth: '1px',
            borderColor: color.lineStrong,
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: button.outlineBorderHover,
          },
          // The accent ring is `box-shadow` on the root, as the design draws
          // it; MUI's own focus treatment is to thicken the outline to 2px,
          // which would land a heavier edge inside the ring.
          '&.Mui-focused': {
            boxShadow: `0 0 0 3px ${color.accentSoft}`,
            '& .MuiOutlinedInput-notchedOutline': {
              borderWidth: '1px',
              borderColor: color.accent,
            },
          },
          /*
            The error edge has to be restated under focus. The `.Mui-focused`
            rule above is a later override than MUI's own `.Mui-error`, so
            without this an invalid field being corrected paints the accent
            border *inside* the red glow.
          */
          '&.Mui-error.Mui-focused': {
            boxShadow: `0 0 0 3px ${color.dangerSoft}`,
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: color.danger,
            },
          },
        },
        input: {
          fontSize: '13.5px',
          color: color.ink,
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          fontSize: '13.5px',
          color: color.ink3,
          '&.Mui-focused': { color: color.accent },
        },
      },
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          fontSize: '12px',
          marginLeft: 0,
        },
      },
    },
  },
});
